import React, { useRef, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';

const LIVERY_STYLES = [
  {
    id: 1,
    name: 'Classic',
    description: 'Full fuselage color with accent tail',
    fuselageColor: true,
    tailColor: true,
    stripe: false,
  },
  {
    id: 2,
    name: 'Modern',
    description: 'White fuselage with colored tail and stripe',
    fuselageColor: false,
    tailColor: true,
    stripe: true,
  },
  {
    id: 3,
    name: 'Minimal',
    description: 'Clean white with accent tail only',
    fuselageColor: false,
    tailColor: true,
    stripe: false,
  },
];

// Livery preview component using canvas for color application
function LiveryPreview({ primaryColor, accentColor, style, iata }) {
  const canvasRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const imageRef = useRef(null);

  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imageRef.current = img;
      setImageLoaded(true);
    };
    img.src = '/aircraft-livery-base.png';
  }, []);

  useEffect(() => {
    if (!imageLoaded || !canvasRef.current || !imageRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = imageRef.current;

    // Set canvas size to match image aspect ratio
    const scale = 400 / img.width;
    canvas.width = 400;
    canvas.height = img.height * scale;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw base image
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    // Get image data for manipulation
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    // Parse colors
    const primary = hexToRgb(primaryColor);
    const accent = hexToRgb(accentColor);

    // Define regions based on canvas coordinates
    const tailStartX = canvas.width * 0.75; // Tail starts at 75% from left
    const stripeY = canvas.height * 0.45; // Stripe at 45% from top
    const stripeHeight = canvas.height * 0.08;

    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3];

      // Skip transparent or very dark pixels
      if (a < 50 || (r < 30 && g < 30 && b < 30)) continue;

      const x = (i / 4) % canvas.width;
      const y = Math.floor((i / 4) / canvas.width);

      // Check if pixel is in "colorable" area (not windows, not shadow)
      const brightness = (r + g + b) / 3;
      const isLight = brightness > 180; // Light colored pixels (white/light gray)
      const isBlue = b > r + 30 && b > g + 30; // Blue pixels (tail)

      if (isBlue || x > tailStartX) {
        // Tail area - apply accent color
        if (style.tailColor && (isLight || isBlue)) {
          const factor = brightness / 255;
          data[i] = Math.round(accent.r * factor);
          data[i + 1] = Math.round(accent.g * factor);
          data[i + 2] = Math.round(accent.b * factor);
        }
      } else if (style.stripe && y > stripeY && y < stripeY + stripeHeight && isLight) {
        // Stripe area - apply accent color
        const factor = brightness / 255;
        data[i] = Math.round(accent.r * factor);
        data[i + 1] = Math.round(accent.g * factor);
        data[i + 2] = Math.round(accent.b * factor);
      } else if (style.fuselageColor && isLight) {
        // Fuselage area - apply primary color
        const factor = brightness / 255;
        data[i] = Math.round(primary.r * factor);
        data[i + 1] = Math.round(primary.g * factor);
        data[i + 2] = Math.round(primary.b * factor);
      }
    }

    ctx.putImageData(imageData, 0, 0);

    // Draw airline code on fuselage
    if (iata) {
      ctx.font = 'bold 24px Arial';
      ctx.fillStyle = style.fuselageColor ? accentColor : primaryColor;
      ctx.textAlign = 'center';
      ctx.fillText(iata, canvas.width * 0.4, canvas.height * 0.55);
    }

  }, [imageLoaded, primaryColor, accentColor, style, iata]);

  return (
    <div className="flex items-center justify-center p-4 rounded-sm" style={{ backgroundColor: 'var(--bg-dark)' }}>
      <canvas
        ref={canvasRef}
        style={{ maxWidth: '100%', height: 'auto' }}
      />
    </div>
  );
}

function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : { r: 255, g: 255, b: 255 };
}

export default function StepLivery({ form, setForm, next, back }) {
  const canProceed = form.liveryId !== null;
  const selectedStyle = LIVERY_STYLES.find(s => s.id === form.liveryId) || LIVERY_STYLES[0];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-light mb-2" style={{ color: 'var(--text-primary)' }}>
          Livery Design
        </h1>
        <p className="text-sm font-light" style={{ color: 'var(--text-secondary)' }}>
          Choose how your fleet colors will be applied.
        </p>
      </div>

      {/* Main Preview */}
      <div className="p-6 rounded-sm" style={{ backgroundColor: 'var(--bg-panel)', borderWidth: '1px', borderColor: 'var(--border)' }}>
        <LiveryPreview
          primaryColor={form.colors.primary}
          accentColor={form.colors.accent}
          style={selectedStyle}
          iata={form.iata}
        />
      </div>

      {/* Style Selection */}
      <div className="grid grid-cols-3 gap-3">
        {LIVERY_STYLES.map((style) => {
          const isSelected = form.liveryId === style.id;
          return (
            <button
              key={style.id}
              onClick={() => setForm({ ...form, liveryId: style.id })}
              className="p-4 rounded-sm transition-all text-center"
              style={{
                borderWidth: '1px',
                borderColor: isSelected ? 'var(--border-strong)' : 'var(--border)',
                backgroundColor: isSelected ? 'var(--neon-dim)' : 'var(--bg-panel)',
              }}
            >
              <div className="text-sm font-medium mb-1" style={{ color: isSelected ? 'var(--neon)' : 'var(--text-primary)' }}>
                {style.name}
              </div>
              <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                {style.description}
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex justify-between pt-4" style={{ borderTopWidth: '1px', borderColor: 'var(--border)' }}>
        <Button
          onClick={back}
          variant="outline"
          className="font-normal px-8 rounded-sm"
          style={{ borderColor: 'var(--border)', color: 'var(--text-primary)' }}
        >
          Back
        </Button>
        <Button
          onClick={next}
          disabled={!canProceed}
          className="font-normal px-8 rounded-sm"
          style={{ backgroundColor: 'var(--neon)', color: '#000' }}
        >
          Continue
        </Button>
      </div>
    </div>
  );
}